/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';
import type { DialogProps } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
};

export type AsyncJobStartedDialogProps = {
  open: boolean;
  onClose: () => void;
  heading: string;
  /** @default medium */
  width?: DialogProps['width'];
  /** Shown as the info banner's title, e.g. a note about a confirmation email. */
  notificationTitle?: string;
  /** Non-blocking warning shown below the info banner, e.g. a partial-failure note. */
  warningMessage?: string;
};

/**
 * Shown once a bulk operation has been kicked off to process in the background — there's no
 * per-row result yet, only a job-started response, so this just tells the admin it's running and
 * lets them dismiss the flow. Shared by AddToGroupModal (select-all add) and the CSV invite flow
 * (ProcessImportAsync).
 */
const AsyncJobStartedDialog = ({
  open,
  onClose,
  heading,
  width,
  notificationTitle,
  warningMessage,
}: AsyncJobStartedDialogProps) => (
  <Dialog open={open} onClose={onClose} width={width}>
    <HeadingGroup>{heading}</HeadingGroup>
    <Content css={styles.content}>
      <Typography2 component="p">{_t('Large batches can take a few minutes to process.')}</Typography2>
      <InlineNotification severity="information" title={notificationTitle}>
        {_t("Feel free to close this, we'll take it from here.")}
      </InlineNotification>
      {/* Always-present live region so screen readers announce the warning when it appears. */}
      <div role="alert">
        {warningMessage && (
          <InlineNotification severity="warning">
            <Typography2 component="p">{warningMessage}</Typography2>
          </InlineNotification>
        )}
      </div>
    </Content>
    <Actions>
      <Button variant="primary" onClick={onClose}>
        {_t('Close')}
      </Button>
    </Actions>
  </Dialog>
);

export default AsyncJobStartedDialog;
